import { ISettings } from "interfaces";
import Cookies from "js-cookie";

import React from "react";
import { createContext, useContext, useState, ReactNode } from "react";
import { SessionType } from "types";

const SESSION_NAME = "potluck.userSession";

interface SessionContextType {
  user: SessionType | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  login: (user: SessionType) => Promise<void>;
  logout: () => void;
  updateSettings: (settings: ISettings) => void;
}

const initialState = {
  user: Cookies.get(SESSION_NAME)
    ? JSON.parse(Cookies.get(SESSION_NAME) || "")
    : null,
  isAuthenticated: !!Cookies.get(SESSION_NAME),
  isGuest: true,
};

const SessionContext = createContext<SessionContextType>({
  user: initialState.user,
  isAuthenticated: initialState.isAuthenticated,
  isGuest: initialState.isGuest,
  login: async (user: SessionType) => {},
  logout: () => {},
  updateSettings: (settings: ISettings) => {},
});

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<SessionType | null>(initialState.user);

  const isAuthenticated = !!user;
  const isGuest = isAuthenticated ? user.role === "guest" : false;

  const updateSessionCookies = (user: SessionType) => {
    // Store user session in a cookie
    Cookies.set(SESSION_NAME, JSON.stringify(user), {
      expires: 7, // Keep session alive for 7 days
      secure: true, // Ensures cookie is only sent over HTTPS
      sameSite: "Strict",
    });
    return user;
  };

  const updateSettings = (settings: ISettings) => {
    const updatedUser = {
      ...user,
      settings: settings,
    };
    return setUser(updateSessionCookies(updatedUser));
  };

  const login = async (user: SessionType) =>
    setUser((_) => updateSessionCookies(user));

  const logout = () => {
    Cookies.remove(SESSION_NAME); // Remove session cookie
    setUser(null);
  };

  const MemoizedValue = React.useMemo(() => {
    const value: SessionContextType = {
      isAuthenticated,
      isGuest,
      user,
      login,
      logout,
      updateSettings,
    };
    return value;
  }, [isAuthenticated, isGuest, user, updateSettings]);

  return (
    <SessionContext.Provider value={MemoizedValue}>
      {children}
    </SessionContext.Provider>
  );
};
